/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import type { CKEditor5 } from '@ckeditor/ckeditor5-metadata';
import type { RefObject } from 'react';

import type { CKEditor5Props } from '../../types/CKEditor5Props';
import { getEditorClassByType } from '../getEditorClassByType/getEditorClassByType';
import { isDecoupledEditor } from '../isDecoupledEditor/isDecoupledEditor';
import type { ToolbarOptions } from '../../types/ToolbarsOptions';
import type { EditorPlugins } from '../../types/EditorPlugins';
import { requiresWordCountPlugin } from '../requiresWordCountPlugin/requiresWordCountPlugin';

type LoadEditorOptions = {
	editorType: CKEditor5Props[ 'editorType' ];
	editorConfig: CKEditor5.EditorConfig;
	toolbarOptions: ToolbarOptions;
	editorPlugins: EditorPlugins;
};

type LoadEditorRefs = {
	sourceElement: RefObject<HTMLElement>;
	decoupledToolbarElement: RefObject<HTMLElement>;
	decoupledMenuBarElement: RefObject<HTMLElement>;
	wordCountElement: RefObject<HTMLElement>;
};

export async function loadEditor( options: LoadEditorOptions, refs: LoadEditorRefs ): Promise<CKEditor5.Editor> {
	const EditorClass = getEditorClassByType( options.editorType );
	const editor = await EditorClass.create( refs.sourceElement.current!, options.editorConfig );

	if ( requiresWordCountPlugin( options.editorPlugins ) ) {
		const wordCountPlugin = editor.plugins.get( 'WordCount' );

		refs.wordCountElement.current?.appendChild( wordCountPlugin.wordCountContainer );
	}

	if ( isDecoupledEditor( options.editorType ) ) {
		const decoupledEditor = editor as CKEditor5.DecoupledEditor;

		refs.decoupledToolbarElement.current?.appendChild( decoupledEditor.ui.view.toolbar.element! );

		if ( options.toolbarOptions.isMenuBarUsed ) {
			refs.decoupledMenuBarElement.current?.appendChild( decoupledEditor.ui.view.menuBarView.element! );
		}
	}

	return editor;
}
